import { createSlice } from "@reduxjs/toolkit";
import {
    getMarketingPopups,
    getMarketingPopupById,
    createMarketingPopup,
    updateMarketingPopup,
    deleteMarketingPopup,
    updateMarketingPopupStatus,
} from "../Actions/MarketingPopupAction";
import { MarketingPopupState } from "../../modules/shared/model/MarketingPopupInterfaces";

const initialState: MarketingPopupState = {
    marketingPopups: [],
    currentMarketingPopup: null,
    error: null,
    loading: false,
    pagination: {
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        hasNextPage: false,
        itemsPerPage: 10,
    },
};

const marketingPopupSlice = createSlice({
    name: "marketingPopup",
    initialState,
    reducers: {
        clearCurrentMarketingPopup: (state) => {
            state.currentMarketingPopup = null;
        },
        clearMarketingPopupError: (state) => {
            state.error = null;
        },
        setMarketingPopupLoading: (state, action) => {
            state.loading = action.payload;
        },
    },
    extraReducers: builder => {
        builder
            // Get all marketing popups
            .addCase(getMarketingPopups.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getMarketingPopups.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload) {
                    state.marketingPopups = action.payload.data || [];
                    state.pagination = {
                        currentPage: action.meta.arg?.page || 1,
                        totalPages: Math.ceil((action.payload.totalCount || 0) / (action.meta.arg?.limit || 10)),
                        totalCount: action.payload.totalCount || 0,
                        hasNextPage: action.payload.hasNextPage || false,
                        itemsPerPage: action.meta.arg?.limit || 10,
                    };
                    state.error = null;
                } else {
                    state.marketingPopups = [];
                }
            })
            .addCase(getMarketingPopups.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            
            // Get single marketing popup by ID
            .addCase(getMarketingPopupById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getMarketingPopupById.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload) {
                    state.currentMarketingPopup = action.payload;
                    state.error = null;
                } else {
                    state.currentMarketingPopup = null;
                }
            })
            .addCase(getMarketingPopupById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            
            // Create marketing popup
            .addCase(createMarketingPopup.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createMarketingPopup.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload) {
                    state.marketingPopups = [...state.marketingPopups, action.payload];
                    state.error = null;
                }
            })
            .addCase(createMarketingPopup.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            
            // Update marketing popup
            .addCase(updateMarketingPopup.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateMarketingPopup.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload) {
                    const updatedPopup = action.payload;
                    const index = state.marketingPopups.findIndex(item => item.id === updatedPopup.id);
                    if (index !== -1) {
                        state.marketingPopups[index] = updatedPopup;
                    }
                    
                    // Update current marketing popup if it's the one being updated
                    if (state.currentMarketingPopup && state.currentMarketingPopup.id === updatedPopup.id) {
                        state.currentMarketingPopup = updatedPopup;
                    }
                    state.error = null;
                }
            })
            .addCase(updateMarketingPopup.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            
            // Delete marketing popup
            .addCase(deleteMarketingPopup.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteMarketingPopup.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload) {
                    state.marketingPopups = state.marketingPopups.filter(item => item.id !== action.payload.id);
                    
                    // Clear current marketing popup if it's the one being deleted
                    if (state.currentMarketingPopup && state.currentMarketingPopup.id === action.payload.id) {
                        state.currentMarketingPopup = null;
                    }
                    state.error = null;
                }
            })
            .addCase(deleteMarketingPopup.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            
            // Update marketing popup status
            .addCase(updateMarketingPopupStatus.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateMarketingPopupStatus.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload) {
                    const updatedPopup = action.payload;
                    const index = state.marketingPopups.findIndex(item => item.id === updatedPopup.id);
                    if (index !== -1) {
                        state.marketingPopups[index] = updatedPopup;
                    }
                    
                    // Update current marketing popup if it's the one being updated
                    if (state.currentMarketingPopup && state.currentMarketingPopup.id === updatedPopup.id) {
                        state.currentMarketingPopup = updatedPopup;
                    }
                    state.error = null;
                }
            })
            .addCase(updateMarketingPopupStatus.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { 
    clearCurrentMarketingPopup, 
    clearMarketingPopupError, 
    setMarketingPopupLoading 
} = marketingPopupSlice.actions;

export default marketingPopupSlice.reducer;
